// @ts-strict
import {
  AksIcon,
  CivoIcon,
  DigitalOceanIcon,
  EksIcon,
  GkeIcon,
  IbmCloudIcon,
  K3sIcon,
  KindIcon,
  KubernetesIcon,
  KurlIcon,
  LinodeIcon,
  MicroK8sIcon,
  OpenShiftIcon,
  OracleIcon,
  Platform9Icon,
  RancherIcon,
  SpectroCloudIcon,
  TanzuIcon,
  EmbeddedClusterIcon,
  UbuntuIcon,
  AlmaLinuxIcon
} from "@/components/shared/IconsBrands";
import {
  golangDurationToEnglish,
  golangDurationToMinutes
} from "@/utilities/VendorUtilities";
import startCase from "lodash/startCase";
import { Link } from "react-router-dom";

// Mapping of OpenShift/OKD versions to Kubernetes versions
export const openshiftToK8sVersionMap: Record<string, string> = {
  "4.20.0-okd": "1.33",
  "4.19.0-okd": "1.32",
  "4.18.0-okd": "1.31",
  "4.17.0-okd": "1.30",
  "4.16.0-okd": "1.29",
  "4.15.0-okd": "1.28",
  "4.14.0-okd": "1.27",
  "4.13.0-okd": "1.26",
};

export const distros = [
  {
    shortName: "aks",
    label: "Azure AKS",
    instanceTypeKey: "aks",
    renderIcon: () => <AksIcon className="w-4" />,
    renderIconLarge: () => <AksIcon className="h-4 md:h-6" />
  },
  {
    shortName: "alibabak8s",
    label: "Alibaba Cloud Kubernetes",
    instanceTypeKey: "alibaba",
    renderIcon: () => <IbmCloudIcon className="w-4" />,
    renderIconLarge: () => <IbmCloudIcon className="h-6" />
  },
  {
    shortName: "civo",
    label: "Civo Kubernetes",
    instanceTypeKey: "civo",
    renderIcon: () => <CivoIcon className="w-4" />,
    renderIconLarge: () => <CivoIcon className="h-6" />
  },
  {
    shortName: "digitalocean",
    label: "DigitalOcean Kubernetes",
    instanceTypeKey: "digitalocean",
    renderIcon: () => <DigitalOceanIcon className="w-4" />,
    renderIconLarge: () => <DigitalOceanIcon className="h-6" />
  },
  {
    shortName: "eks",
    label: "Amazon EKS",
    instanceTypeKey: "eks",
    renderIcon: () => <EksIcon className="w-4" />,
    renderIconLarge: () => <EksIcon className="h-6" />
  },
  //  these distros are for development only
  {
    shortName: "fake",
    label: "Fake",
    instanceTypeKey: "replicated",
    renderIcon: () => <KubernetesIcon className="w-4" />,
    renderIconLarge: () => <KubernetesIcon className="h-6" />
  },
  {
    shortName: "fakevm",
    label: "Fake VM",
    instanceTypeKey: "replicated",
    renderIcon: () => <KubernetesIcon className="w-4" />,
    renderIconLarge: () => <KubernetesIcon className="h-6" />
  },
  {
    shortName: "gke",
    label: "Google GKE",
    instanceTypeKey: "gke",
    renderIcon: () => <GkeIcon className="w-5" />,
    renderIconLarge: () => <GkeIcon className="h-6" />
  },
  {
    shortName: "embedded-cluster",
    label: "Embedded Cluster v2",
    instanceTypeKey: "replicated",
    renderIcon: () => <EmbeddedClusterIcon className="w-4" />,
    renderIconLarge: () => <EmbeddedClusterIcon className="h-6" />
  },
  {
    shortName: "iks",
    label: "IBM Cloud Kubernetes",
    instanceTypeKey: "iks",
    renderIcon: () => <IbmCloudIcon className="w-4" />,
    renderIconLarge: () => <IbmCloudIcon className="h-6" />
  },
  {
    shortName: "k3s",
    label: "k3s",
    instanceTypeKey: "replicated",
    renderIcon: () => <K3sIcon className="w-4" />,
    renderIconLarge: () => <K3sIcon className="h-6" />
  },
  {
    shortName: "rke2",
    label: "RKE2",
    instanceTypeKey: "replicated",
    renderIcon: () => <RancherIcon className="w-4" />, // TODO
    renderIconLarge: () => <RancherIcon className="h-6" /> // TODO
  },
  {
    shortName: "kind",
    label: "kind",
    instanceTypeKey: "replicated",
    renderIcon: () => <KindIcon className="w-6" />,
    renderIconLarge: () => <KindIcon className="h-6" />
  },
  {
    shortName: "kurl",
    label: "kURL",
    instanceTypeKey: "replicated",
    renderIcon: () => <KurlIcon className="w-4" />,
    renderIconLarge: () => <KurlIcon className="h-6" />
  },
  {
    shortName: "linode",
    label: "Linode Kubernetes",
    instanceTypeKey: "linode",
    renderIcon: () => <LinodeIcon className="w-4" />,
    renderIconLarge: () => <LinodeIcon className="h-6" />
  },
  {
    shortName: "microk8s",
    label: "MicroK8s",
    instanceTypeKey: "canonical",
    renderIcon: () => <MicroK8sIcon className="w-4" />,
    renderIconLarge: () => <MicroK8sIcon className="h-6" />
  },
  {
    shortName: "oke",
    label: "OKE",
    instanceTypeKey: "oke",
    renderIcon: () => <OracleIcon className="w-4" />,
    renderIconLarge: () => <OracleIcon className="h-6" />
  },
  {
    shortName: "openshift",
    label: "Red Hat OpenShift",
    instanceTypeKey: "replicated",
    renderIcon: () => <OpenShiftIcon className="w-4" />,
    renderIconLarge: () => <OpenShiftIcon className="h-6" />
  },
  {
    shortName: "platform9",
    label: "Platform9 Kubernetes",
    instanceTypeKey: "platform9",
    renderIcon: () => <Platform9Icon className="w-4" />,
    renderIconLarge: () => <Platform9Icon className="h-6" />
  },
  {
    shortName: "rancher",
    label: "Rancher",
    instanceTypeKey: "rancher",
    renderIcon: () => <RancherIcon className="w-4" />,
    renderIconLarge: () => <RancherIcon className="h-6" />
  },
  {
    shortName: "spectrocloud",
    label: "Spectro Cloud Kubernetes",
    instanceTypeKey: "spectrocloud",
    renderIcon: () => <SpectroCloudIcon className="w-4" />,
    renderIconLarge: () => <SpectroCloudIcon className="h-6" />
  },
  {
    shortName: "tanzu",
    label: "VMware Tanzu",
    instanceTypeKey: "vmware",
    renderIcon: () => <TanzuIcon className="w-4" />,
    renderIconLarge: () => <TanzuIcon className="h-6" />
  },
  {
    shortName: "ubuntu",
    label: "Ubuntu",
    instanceTypeKey: "replicated",
    renderIcon: () => <UbuntuIcon className="w-4" />,
    renderIconLarge: () => <UbuntuIcon className="h-6" />
  },
  {
    shortName: "almalinux",
    label: "AlmaLinux",
    instanceTypeKey: "replicated",
    renderIcon: () => <AlmaLinuxIcon className="w-4" />,
    renderIconLarge: () => <AlmaLinuxIcon className="h-6" />
  }
];

export const generateTTLOptions = (
  defaultTTLOptions: string[],
  maxTTL: string,
  runningAt?: string
): { value: string; label: string }[] => {
  const alwaysTTLOptions = defaultTTLOptions.filter(ttl => {
    if (golangDurationToMinutes(ttl) > golangDurationToMinutes(maxTTL)) {
      return false;
    }

    return true;
  });

  const ttlOptions = alwaysTTLOptions.map(ttl => {
    const baseLabel = golangDurationToEnglish(ttl);

    // If runningAt is provided, calculate the new expiration time
    if (runningAt) {
      const runningDate = new Date(runningAt);
      const ttlMinutes = golangDurationToMinutes(ttl);
      const newExpiration = new Date(runningDate.getTime() + ttlMinutes * 60 * 1000);

      // Format the date as "MMM DD, YYYY HH:MM"
      const formattedDate = newExpiration.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      return {
        value: ttl,
        label: `${baseLabel} (expires ${formattedDate})`
      };
    }

    return {
      value: ttl,
      label: baseLabel
    };
  });

  // if the TTL is not in the always TTL, add it to the ttlOptions
  if (!alwaysTTLOptions.includes(maxTTL)) {
    const baseLabel = golangDurationToEnglish(maxTTL);

    if (runningAt) {
      const runningDate = new Date(runningAt);
      const ttlMinutes = golangDurationToMinutes(maxTTL);
      const newExpiration = new Date(runningDate.getTime() + ttlMinutes * 60 * 1000);

      const formattedDate = newExpiration.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      ttlOptions.push({
        value: maxTTL,
        label: `${baseLabel} (expires ${formattedDate})`
      });
    } else {
      ttlOptions.push({
        value: maxTTL,
        label: baseLabel
      });
    }
  }

  // sort by the value, parsed duration, shortest first
  ttlOptions.sort((a, b) => {
    // convert the .value to minutes
    const aMinutes = golangDurationToMinutes(a.value);
    const bMinutes = golangDurationToMinutes(b.value);

    // if they are the same, sort by the label
    if (aMinutes === bMinutes) {
      return a.label.localeCompare(b.label);
    }

    // otherwise sort by the minutes
    return aMinutes - bMinutes;
  });

  return ttlOptions;
};

export const isUnknownDistro = (distro: string): boolean => {
  const isKind = distro === "kind";
  const isEks = distro === "eks";
  const isGke = distro === "gke";
  const isAks = distro === "aks";
  const isOke = distro === "oke";
  const isK3s = distro === "k3s";
  const isRke2 = distro === "rke2";
  const isKurl = distro === "kurl";
  const isEmbeddedCluster = distro === "embedded-cluster";
  const isOpenshift = distro === "openshift";
  return (
    !isKind &&
    !isEks &&
    !isGke &&
    !isAks &&
    !isOke &&
    !isK3s &&
    !isRke2 &&
    !isKurl &&
    !isEmbeddedCluster &&
    !isOpenshift
  );
};

export const formatSchedulingStatus = (
  status: string
): string | React.ReactNode => {
  switch (status) {
    case "not-attempted":
      return "Waiting for an available server.";
    case "insufficient-credits":
      return (
        <>
          Insufficient credits. You can add more credits by clicking
          <br />
          the 'Buy credits' button above.
        </>
      );
    case "no-provisioners":
      return "Waiting for an available server.";
    case "exceeds-limits":
      return (
        <>
          Quota exceeded. You can manage your quota on the{" "}
          <Link
            to="/compatibility-matrix/settings"
            className="text-blue-300 font-semibold hover:underline cursor-pointer"
          >
            Settings
          </Link>{" "}
          page.
        </>
      );
    default:
      return startCase(status);
  }
};
